import { EProductCardForm } from '@/domain/product/EProductCardForm';
import { EProductFormat } from '@/domain/product/EProductFormat';
import { svgBoundsToSceneMatrix } from '@/infrastructure/engine3d/scene/card3dSceneHelpers';
import { SVGLoader } from 'three/addons/loaders/SVGLoader.js';
import { BoxGeometry, CanvasTexture, ExtrudeGeometry, Group, Mesh, MeshStandardMaterial, Vector2, type Texture } from 'three';
import type { TCardDimensions } from '@/infrastructure/engine3d/strategies/card/CardLightingRig';

// 1×1 white canvas — used as an initial placeholder .map so the
// material compiles with USE_MAP=1 from the start (matching main's
// order). The DescriptorTextureApplier later swaps this out for the
// real fabric snapshot texture. Without a placeholder, the material
// is first compiled without USE_MAP; the later swap flips the define
// and, in practice, prevents normalMap sampling from being honored on
// certain drivers.
function _makePlaceholderMap(): CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 1, 1);
    }
    return new CanvasTexture(canvas);
}

// Face indices — folded BoxGeometry: 4=recto, 5=verso.
// Cut ExtrudeGeometry (post-split): 0=recto, 2=verso.
type TMatRef = { faceIndex: number; mesh: Mesh };

type TBuildResult = {
    dimensions: TCardDimensions;
    materials: MeshStandardMaterial[];
    matRefs: TMatRef[];
    // Only present for the folded card; used by the fold-lerp update.
    rightPivot: Group | null;
    root: Group;
};

type TBuildParams = {
    // Domain-level branch — Folded = 2 hinged BoxGeometry panels;
    // CutShape = single ExtrudeGeometry from cuttingPathUrl.
    cardForm: EProductCardForm;
    cuttingPathUrl: string | null;
    paperNormal: Texture | null;
    paperRoughness: Texture | null;
    productFormat: EProductFormat;
    productHeightInMm: number | undefined;
    productWidthInMm: number | undefined;
};

const CARD_GROUP_NAME = 'cardGroup';
const CARD_MAX_SIZE = 2.4;
const CARD_DEPTH = 0.006;
const CUT_SHAPE_CURVE_SEGMENTS = 32;
const SVG_TO_CANVAS = 300 / 72;
const PIXELS_PER_MM_AT_300_DPI = 300 / 25.4;
const MAT_PROPS = { envMapIntensity: 0, metalness: 0, roughness: 0.75 } as const;

// cardForm=Folded → 2 hinged panels (BoxGeometry + pivot Group).
// cardForm=CutShape → single ExtrudeGeometry from the SVG cutting
// path (which MUST be present — enforced here).
async function buildCardMesh(params: TBuildParams): Promise<TBuildResult> {
    if (params.cardForm === EProductCardForm.CutShape) {
        if (!params.cuttingPathUrl) {
            throw new Error('CardMeshBuilder: cardForm=CutShape requires a cuttingPathUrl');
        }
        return _buildCutShape(params, params.cuttingPathUrl);
    }
    return _buildFoldedCard(params);
}

function _paperProps(normal: Texture | null, roughness: Texture | null) {
    return {
        ...(normal ? { normalMap: normal, normalScale: new Vector2(1.2, 1.2) } : {}),
        ...(roughness ? { roughnessMap: roughness } : {}),
    };
}

function _buildFoldedCard(params: TBuildParams): TBuildResult {
    const paperProps = _paperProps(params.paperNormal, params.paperRoughness);
    const aspect = params.productWidthInMm && params.productHeightInMm ? params.productWidthInMm / params.productHeightInMm : 1.414;
    const cardW = aspect >= 1 ? CARD_MAX_SIZE : CARD_MAX_SIZE * aspect;
    const cardH = aspect >= 1 ? CARD_MAX_SIZE / aspect : CARD_MAX_SIZE;
    const halfW = cardW / 2;

    const edgeMat = new MeshStandardMaterial({ color: 0xfafafa, ...MAT_PROPS });
    const whiteMat = new MeshStandardMaterial({ color: 0xf8f8f8, ...MAT_PROPS, ...paperProps });
    const materials: MeshStandardMaterial[] = [edgeMat, whiteMat];

    const rectoLeftMat = new MeshStandardMaterial({ map: _makePlaceholderMap(), ...MAT_PROPS, ...paperProps });
    const rectoRightMat = new MeshStandardMaterial({ map: _makePlaceholderMap(), ...MAT_PROPS, ...paperProps });
    const isRectoVerso = params.productFormat === EProductFormat.RECTO_VERSO;
    const versoLeftMat = isRectoVerso ? new MeshStandardMaterial({ map: _makePlaceholderMap(), ...MAT_PROPS, ...paperProps }) : whiteMat;
    const versoRightMat = isRectoVerso ? new MeshStandardMaterial({ map: _makePlaceholderMap(), ...MAT_PROPS, ...paperProps }) : whiteMat;

    const trackedMats: MeshStandardMaterial[] = [rectoLeftMat, rectoRightMat];
    if (versoLeftMat !== whiteMat) {
        trackedMats.push(versoLeftMat);
    }
    if (versoRightMat !== whiteMat) {
        trackedMats.push(versoRightMat);
    }
    materials.push(...trackedMats);

    const leftMesh = new Mesh(new BoxGeometry(halfW, cardH, CARD_DEPTH), [edgeMat, edgeMat, edgeMat, edgeMat, rectoLeftMat, versoLeftMat]);
    leftMesh.position.x = -halfW / 2;
    leftMesh.castShadow = true;

    const rightMesh = new Mesh(new BoxGeometry(halfW, cardH, CARD_DEPTH), [
        edgeMat,
        edgeMat,
        edgeMat,
        edgeMat,
        rectoRightMat,
        versoRightMat,
    ]);
    rightMesh.position.x = halfW / 2;
    rightMesh.castShadow = true;

    const matRefs: TMatRef[] = [
        { mesh: leftMesh, faceIndex: 4 },
        { mesh: rightMesh, faceIndex: 4 },
    ];
    if (versoLeftMat !== whiteMat) {
        matRefs.push({ mesh: leftMesh, faceIndex: 5 });
    }
    if (versoRightMat !== whiteMat) {
        matRefs.push({ mesh: rightMesh, faceIndex: 5 });
    }

    const rightPivot = new Group();
    rightPivot.add(rightMesh);

    const root = new Group();
    root.name = CARD_GROUP_NAME;
    root.add(leftMesh);
    root.add(rightPivot);
    root.position.y = cardH / 2;

    return { dimensions: { cardH, cardW }, materials, matRefs, rightPivot, root };
}

async function _buildCutShape(params: TBuildParams, cuttingPathUrl: string): Promise<TBuildResult> {
    const paperProps = _paperProps(params.paperNormal, params.paperRoughness);
    const emptyRoot = new Group();
    emptyRoot.name = CARD_GROUP_NAME;

    let cardW = CARD_MAX_SIZE;
    let cardH = CARD_MAX_SIZE;

    try {
        const loader = new SVGLoader();
        const svgData = await loader.loadAsync(cuttingPathUrl);
        const shapes = svgData.paths.flatMap((path) => path.toShapes(true));
        if (shapes.length === 0) {
            console.error('CardMeshBuilder: SVG cutting path contains no shapes');
            return { dimensions: { cardH, cardW }, materials: [], matRefs: [], rightPivot: null, root: emptyRoot };
        }

        const bounds = _svgBounds(shapes);
        const svgW = bounds.maxX - bounds.minX;
        const svgH = bounds.maxY - bounds.minY;
        if (svgW === 0 || svgH === 0) {
            console.error('CardMeshBuilder: SVG cutting path has zero-size bounding box');
            return { dimensions: { cardH, cardW }, materials: [], matRefs: [], rightPivot: null, root: emptyRoot };
        }

        const svgAspect = svgW / svgH;
        cardW = svgAspect >= 1 ? CARD_MAX_SIZE : CARD_MAX_SIZE * svgAspect;
        cardH = svgAspect >= 1 ? CARD_MAX_SIZE / svgAspect : CARD_MAX_SIZE;

        const svgCenterX = (bounds.minX + bounds.maxX) / 2;
        const svgCenterY = (bounds.minY + bounds.maxY) / 2;
        // productWidthInMm is in millimetres; convert to canvas pixels
        // at 300 DPI (the print target), then to SVG viewport units at
        // 72 DPI so the UV mapping aligns the print area onto the SVG
        // shape. Fallback to svgW * SVG_TO_CANVAS keeps the original
        // shape-only behaviour when product dimensions are unknown.
        const imageRenderW = params.productWidthInMm ? params.productWidthInMm * PIXELS_PER_MM_AT_300_DPI : svgW * SVG_TO_CANVAS;
        const imageRenderH = params.productHeightInMm ? params.productHeightInMm * PIXELS_PER_MM_AT_300_DPI : svgH * SVG_TO_CANVAS;
        const svgPrintW = imageRenderW / SVG_TO_CANVAS;
        const svgPrintH = imageRenderH / SVG_TO_CANVAS;

        const uvGen = _makeUVGenerator(svgCenterX, svgCenterY, svgPrintW, svgPrintH);
        const geometry = new ExtrudeGeometry(shapes, {
            bevelEnabled: false,
            curveSegments: CUT_SHAPE_CURVE_SEGMENTS,
            depth: 1,
            UVGenerator: uvGen,
        });
        geometry.applyMatrix4(
            svgBoundsToSceneMatrix({ minX: bounds.minX, minY: bounds.minY, svgH, svgW }, { cardD: CARD_DEPTH, cardH, cardW }),
        );
        _splitCapGroups(geometry);

        const isRectoVerso = params.productFormat === EProductFormat.RECTO_VERSO;
        const rectoMat = new MeshStandardMaterial({ map: _makePlaceholderMap(), ...MAT_PROPS, ...paperProps });
        const sideMat = new MeshStandardMaterial({ color: 0xfafafa, ...MAT_PROPS });
        const versoMat = isRectoVerso
            ? new MeshStandardMaterial({ map: _makePlaceholderMap(), ...MAT_PROPS, ...paperProps })
            : new MeshStandardMaterial({ color: 0xf8f8f8, ...MAT_PROPS, ...paperProps });
        const materials = [rectoMat, sideMat, versoMat];

        const cutMesh = new Mesh(geometry, materials);
        cutMesh.castShadow = true;

        const matRefs: TMatRef[] = [{ mesh: cutMesh, faceIndex: 0 }];
        if (isRectoVerso) {
            matRefs.push({ mesh: cutMesh, faceIndex: 2 });
        }

        const root = new Group();
        root.name = CARD_GROUP_NAME;
        root.add(cutMesh);
        root.position.y = cardH / 2;

        return { dimensions: { cardH, cardW }, materials, matRefs, rightPivot: null, root };
    } catch (err) {
        console.error('CardMeshBuilder: failed to load SVG cutting path', err);
        return { dimensions: { cardH, cardW }, materials: [], matRefs: [], rightPivot: null, root: emptyRoot };
    }
}

function _svgBounds(shapes: { getPoints(): { x: number; y: number }[] }[]) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    shapes.forEach((shape) => {
        shape.getPoints().forEach((pt) => {
            if (pt.x < minX) {
                minX = pt.x;
            }
            if (pt.x > maxX) {
                maxX = pt.x;
            }
            if (pt.y < minY) {
                minY = pt.y;
            }
            if (pt.y > maxY) {
                maxY = pt.y;
            }
        });
    });
    return { maxX, maxY, minX, minY };
}

function _makeUVGenerator(svgCenterX: number, svgCenterY: number, svgPrintW: number, svgPrintH: number) {
    return {
        generateTopUV(_geom: ExtrudeGeometry, vertices: number[], a: number, b: number, c: number) {
            function toUV(idx: number) {
                return new Vector2(
                    0.5 + (vertices[idx * 3] - svgCenterX) / svgPrintW,
                    0.5 - (vertices[idx * 3 + 1] - svgCenterY) / svgPrintH,
                );
            }
            return [toUV(a), toUV(b), toUV(c)];
        },
        generateSideWallUV(_geom: ExtrudeGeometry, vertices: number[], a: number, b: number, c: number, d: number) {
            const az = vertices[a * 3 + 2];
            const bz = vertices[b * 3 + 2];
            const cz = vertices[c * 3 + 2];
            const dz = vertices[d * 3 + 2];
            const ax = vertices[a * 3];
            const bx = vertices[b * 3];
            const cx = vertices[c * 3];
            const dx = vertices[d * 3];
            const ay = vertices[a * 3 + 1];
            const by = vertices[b * 3 + 1];
            const cy = vertices[c * 3 + 1];
            const dy = vertices[d * 3 + 1];
            if (Math.abs(ay - by) < Math.abs(ax - bx)) {
                return [new Vector2(ax, 1 - az), new Vector2(bx, 1 - bz), new Vector2(cx, 1 - cz), new Vector2(dx, 1 - dz)];
            }
            return [new Vector2(ay, 1 - az), new Vector2(by, 1 - bz), new Vector2(cy, 1 - cz), new Vector2(dy, 1 - dz)];
        },
    };
}

// Split each cap group in half → bottom = recto (0), top = verso (2).
function _splitCapGroups(geometry: ExtrudeGeometry): void {
    const splitGroups: { count: number; materialIndex: number; start: number }[] = [];
    geometry.groups.forEach((group, i) => {
        if (i % 2 === 0) {
            const halfCount = group.count / 2;
            splitGroups.push({ count: halfCount, materialIndex: 0, start: group.start });
            splitGroups.push({ count: halfCount, materialIndex: 2, start: group.start + halfCount });
        } else {
            splitGroups.push({ count: group.count, materialIndex: group.materialIndex ?? 1, start: group.start });
        }
    });
    geometry.groups = splitGroups;
}

export { buildCardMesh };
export type { TMatRef };
